// コメントの投稿者アバター。
// 画像が取得できない / 不正な URL の場合はイニシャル（または既定アイコン）へフォールバックする。
// 投稿者は「他の参加者」由来のことが多いため、https は信頼済み IdP origin の allowlist でのみ許可し、
// data:image/... は常に許可する（avatar-image-url の多層防御方針に合わせる）。

import { AccountCircleIconData } from "@meiden-connect/material-symbols";
import { Icon } from "@meiden-connect/ui";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import {
  getRemoteAvatarAllowedHttpsOrigins,
  isRenderableAvatarImageUrl,
} from "../../../auth/avatar-image-url";
import { getAvatarInitials } from "../../../lib/avatar-initials";

interface Props {
  readonly name: string;
  readonly src?: string | undefined;
  /** 直径（px）。 */
  readonly size: number;
  /** 周囲のリング色（重ね表示で隣のアバターと区切るため）。既定は白。 */
  readonly ringColor?: string | undefined;
  readonly title?: string | undefined;
  /**
   * 自分自身のアバターか。true のとき https の allowlist 制限を外す
   * （自分のアバターは任意 https を許容。他参加者由来は信頼済み IdP origin のみ許可する）。
   */
  readonly isSelf?: boolean | undefined;
}

export function CommentAvatar({ name, src, size, ringColor, title, isSelf = false }: Props): JSX.Element {
  const [errored, setErrored] = useState(false);
  useEffect(() => setErrored(false), [src]);
  const remoteAllowedHttpsOrigins = useMemo(() => getRemoteAvatarAllowedHttpsOrigins(), []);
  // 自分のアバターは任意 https を許容（undefined 指定）。他者のスナップショットは allowlist で制限する。
  const renderOptions = isSelf ? {} : { allowedHttpsOrigins: remoteAllowedHttpsOrigins };
  const showImage =
    src !== undefined &&
    src.length > 0 &&
    isRenderableAvatarImageUrl(src, renderOptions) &&
    !errored;

  const base: CSSProperties = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: "50%",
    boxSizing: "border-box",
    border: `2px solid ${ringColor ?? "#ffffff"}`,
    background: "var(--color-surface, #fff)",
    overflow: "hidden",
    flex: "0 0 auto",
    display: "grid",
    placeItems: "center",
    objectFit: "cover",
  };

  if (showImage) {
    return (
      <img
        src={src}
        alt={name}
        title={title ?? name}
        referrerPolicy="no-referrer"
        // ネイティブの画像ドラッグ（ゴースト画像の生成・ドロップ）を抑止する。
        // これがないとピンをドラッグした際に、移動ではなくアバター画像のドラッグが始まってしまう。
        draggable={false}
        onError={() => setErrored(true)}
        style={base}
      />
    );
  }

  // フォールバックはダッシュボード右上のユーザーアバター（.dashboard-user-avatar-fallback）と
  // 同じ見た目へ揃える: ブランド青の背景・白文字・姓名2文字のイニシャル（例:「山田 花子」→「山花」）。
  // 重ね表示時の区切り用の白リング（base.border）は維持する。
  const initials = getAvatarInitials(name);
  return (
    <span
      aria-hidden="true"
      title={title ?? name}
      style={{
        ...base,
        background: "#2142c0",
        color: "#ffffff",
        fontSize: `${Math.max(9, Math.floor(size * 0.42))}px`,
        fontWeight: 700,
        userSelect: "none",
      }}
    >
      {initials.length > 0 ? initials : <Icon d={AccountCircleIconData} />}
    </span>
  );
}
